import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import {
  DASHBOARD_SUMMARY_TYPES,
  SummaryReportSettingsModel,
  SummaryType,
} from '@main-application/shared/services/summary-report-settings.service';
import {
  addToDashboard,
  addToDashboardFailure,
  addToDashboardSuccess,
  removeFromDashboard,
  removeFromDashboardFailure,
  removeFromDashboardSuccess,
} from '@main-application/store/actions/summary-report-settings.actions';
import { FadeTextComponent } from '@shared/components/ellipsis-one-row/fade-text.component';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { ColorPickerModule } from '@ui-components/components/color-picker/color-picker.module';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { InputErrorModule } from '@ui-components/components/input-error/input-error.module';
import { DateDropdownModule } from '@ui-components/controls/date-dropdown/date-dropdown.module';
import { InputModule } from '@ui-components/controls/input/input.module';
import { InputAreaModule } from '@ui-components/controls/input-area/input-area.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';
import { UserSelectDropdownModule } from '@ui-components/controls/user-select-dropdown/user-select-dropdown.module';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';

interface QuickSummaryConfig {
  display: boolean;
  enabled: boolean;
}

interface EditReportsListModalData {
  allReportList: SummaryReportSettingsModel[];
  roleId: number;
  quickSummaryConfig?: QuickSummaryConfig;
}

@UntilDestroy()
@Component({
  selector: 'app-edit-reports-list-modal',
  standalone: true,
  templateUrl: './edit-reports-list-modal.component.html',
  styleUrls: ['./edit-reports-list-modal.component.scss'],
  imports: [
    NgIf,
    NgFor,
    ReactiveFormsModule,
    MatMenuModule,
    MatButtonModule,
    MatDialogModule,
    MatCheckboxModule,
    RadioButtonDropdownModule,
    UserSelectDropdownModule,
    InputModule,
    InputAreaModule,
    InputErrorModule,
    DateDropdownModule,
    ColorPickerModule,
    MatProgressSpinnerModule,
    FadeTextComponent,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditReportsListModalComponent extends PuSubscribable implements OnInit {
  SummaryType = SummaryType;

  public data = inject<EditReportsListModalData>(MAT_DIALOG_DATA);
  private store = inject(Store);
  private actions$ = inject(Actions);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private dialogRef = inject(MatDialogRef<EditReportsListModalComponent>);
  private snackbarService = inject(SnackbarService);

  public isSaving = false;
  public allOptions: IRadioButtonOption<number>[] = [];
  public filteredOptions: IRadioButtonOption<number>[] = [];
  public shouldDisplayQuickSummaryRow = false;
  public showQuickSummaryRowForFilter = false;
  public quickSummaryEnabled = false;
  public quickSummaryLabel = 'All Tasks';

  private pendingOps = 0;
  private successCount = 0;
  private failureCount = 0;

  public summaryTypesList: IRadioButtonOption<number | string | null>[] = [
    { value: -1, label: 'All types' },
    { value: SummaryType.BB, label: 'Boards' },
    { value: SummaryType.BoardsUsage, label: 'Boards Usage' },
    { value: SummaryType.Inspection, label: 'Inspections' },
    { value: SummaryType.Service, label: 'Service' },
    { value: SummaryType.Turns, label: 'Turns' },
    { value: SummaryType.UserSummary, label: 'Users' },
  ];

  form = this.fb.group({
    id: [null],
    search: [null as string, [Validators.maxLength(255)]],
    summaryType: [-1],
    filterValues: this.fb.group({}),
  });

  ngOnInit(): void {
    if (this.data) {
      this.shouldDisplayQuickSummaryRow = !!this.data.quickSummaryConfig;
      this.quickSummaryEnabled = !!this.data.quickSummaryConfig?.enabled;

      this.updateQuickSummaryVisibility(this.form.get('summaryType')?.value ?? -1);

      this.allOptions = (this.data.allReportList ?? [])
        .filter((s: SummaryReportSettingsModel) => this.isDashboardSummaryType(s.summaryType))
        .map((setting: SummaryReportSettingsModel) => ({
          value: setting.id,
          label: setting.name,
          summaryType: setting.summaryType,
          dataSource: setting.dataSource,
          rolesHaveOnDashboard: setting.rolesHaveOnDashboard,
        }));

      const filterValuesGroup = this.form.get('filterValues') as FormGroup;
      this.allOptions.forEach(option => {
        const checked =
          Array.isArray(option.rolesHaveOnDashboard) && option.rolesHaveOnDashboard.includes(this.data.roleId);
        filterValuesGroup.addControl(option.value.toString(), this.fb.control(checked));
      });

      this.filteredOptions = this.sortOptions(this.allOptions);

      this.form
        .get('summaryType')!
        .valueChanges.subscribe((selectedType: number) => {
          if (selectedType === -1) {
            this.filteredOptions = this.sortOptions(this.allOptions);
          } else {
            this.filteredOptions = this.sortOptions(this.allOptions.filter(o => o.summaryType === selectedType));
          }

          this.updateQuickSummaryVisibility(selectedType);
          this.cdr.markForCheck();
        })
        .untilDestroyed(this);

      this.cdr.markForCheck();

      this.actions$
        .pipe(ofType(addToDashboardSuccess, removeFromDashboardSuccess))
        .subscribe(() => {
          this.pendingOps--;
          this.successCount++;
          this.checkCompletion();
        })
        .untilDestroyed(this);

      this.actions$
        .pipe(ofType(addToDashboardFailure, removeFromDashboardFailure))
        .subscribe(() => {
          this.pendingOps--;
          this.failureCount++;
          this.checkCompletion();
        })
        .untilDestroyed(this);

      this.actions$
        .pipe(ofType(addToDashboardFailure, removeFromDashboardFailure))
        .subscribe(() => {
          this.pendingOps--;
          this.isSaving = false;
          this.cdr.markForCheck();
        })
        .untilDestroyed(this);
    }
  }

  private isDashboardSummaryType(type: SummaryType | null | undefined): type is SummaryType {
    return type !== null && type !== undefined && DASHBOARD_SUMMARY_TYPES.includes(type);
  }

  private sortOptions(options: IRadioButtonOption<number>[]): IRadioButtonOption<number>[] {
    const filterValuesGroup = this.form.get('filterValues') as FormGroup;
    return [...options].sort((a, b) => {
      const aChecked = filterValuesGroup.get(a.value.toString())?.value === true;
      const bChecked = filterValuesGroup.get(b.value.toString())?.value === true;

      if (aChecked !== bChecked) return aChecked ? -1 : 1;

      return a.label.localeCompare(b.label);
    });
  }

  cancel() {
    this.dialogRef.close({
      result: DialogResult.Fail,
      data: null,
    });
  }

  save() {
    if (!this.form) return;

    this.isSaving = true;

    const filterValuesGroup = this.form.get('filterValues') as FormGroup;
    const selectedIds = Object.keys(filterValuesGroup.value)
      .filter(key => filterValuesGroup.value[key])
      .map(Number);
    const roleId = this.data.roleId;

    const originallySelected = this.allOptions
      .filter(opt => Array.isArray(opt.rolesHaveOnDashboard) && opt.rolesHaveOnDashboard.includes(roleId))
      .map(opt => opt.value);

    const toAdd = selectedIds.filter(id => !originallySelected.includes(id));
    const toRemove = originallySelected.filter(id => !selectedIds.includes(id));

    this.pendingOps = 0;

    if (toAdd.length > 0) {
      this.pendingOps++;
      this.store.dispatch(addToDashboard({ model: { roleId, summarySettingsReportIds: toAdd } }));
    }

    if (toRemove.length > 0) {
      this.pendingOps++;
      this.store.dispatch(removeFromDashboard({ model: { roleId, summarySettingsReportIds: toRemove } }));
    }

    if (this.pendingOps === 0) {
      this.isSaving = false;
      this.dialogRef.close(DialogResult.Fail);
    }
  }

  onQuickSummaryClick(event: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.snackbarService.warning(
      'All Tasks visibility is managed by the Role permission Dashboard_ShowWorkloadBlock',
      4000
    );
  }

  private checkCompletion() {
    if (this.pendingOps === 0) {
      this.isSaving = false;
      if (this.failureCount > 0) {
        this.dialogRef.close(DialogResult.Fail);
      } else {
        this.dialogRef.close(DialogResult.Success);
      }
    }
  }

  private updateQuickSummaryVisibility(selectedType: number | null | undefined): void {
    this.showQuickSummaryRowForFilter =
      this.shouldDisplayQuickSummaryRow && (selectedType === -1 || selectedType === null || selectedType === undefined);
  }
}
